import React, { FunctionComponent, ReactElement, ReactNode, useRef, useState } from 'react'
import { View, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { HoverActionContainer } from '../../organisms/hoverActionContainer/HoverActionContainer'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { HoverActionItem } from '../hoverActions/HoverActions'
import { Popover, PopoverProps } from '../popover/Popover'

export type TaskListItemPopoverConfig = {
  title: PopoverProps['title']
  content: ReactNode
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  position?: PopoverProps['placement']
  positionKey?: PopoverProps['positionKey']
}

export type TaskListItemProps = {
  icon: ReactElement
  title: ReactNode
  taskType?: ReactNode
  rightText?: ReactNode
  taskTypeSize?: BodyTextSize
  rightTextSize?: BodyTextSize
  hoverActions?: HoverActionItem[]
  testId?: string
  popover?: TaskListItemPopoverConfig
  onPress?: () => void
  disabled?: boolean
  disabledTooltipContent?: ReactNode
}

const useId = (React as any).useId as () => string

// Stretches the tooltip's anchor across the row so the tooltip is centered above it
const TOOLTIP_ANCHOR_STYLE: ViewStyle = { flex: 1 }

const Container = styled(HoverActionContainer)(({ theme }) => ({
  padding: theme.spacing['16px'],
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const LeftSection = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const IconWrapper = styled.View<{ disabled?: boolean }>(({ theme, disabled }) => ({
  opacity: disabled ? 0.65 : 1,
  backgroundColor: theme.colors.backgroundSection,
  width: theme.spacing['40px'],
  height: theme.spacing['40px'],
  borderRadius: 100,
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing['16px'],
}))

const TextSection = styled.View({
  justifyContent: 'center',
})

const RightSection = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const DisabledDescription = styled.View({
  display: 'none',
})

export const TaskListItem: FunctionComponent<TaskListItemProps> = ({
  icon,
  taskType,
  title,
  rightText,
  taskTypeSize = BodyTextSize.CAPTION,
  rightTextSize = BodyTextSize.CAPTION,
  hoverActions,
  testId,
  popover,
  onPress,
  disabled = false,
  disabledTooltipContent,
}) => {
  const { colors } = useTheme() as ThemeType
  const triggerRef = useRef<View>(null)
  const tooltipRef = useRef<{ show: () => void; hide: () => void }>(null)
  const [isHovered, setIsHovered] = useState(false)
  const disabledDescriptionId = useId()
  const showDisabledTooltip = disabled && !!disabledTooltipContent
  const primaryTextColor = disabled ? colors.textInactive : colors.textPrimary
  const secondaryTextColor = disabled ? colors.textInactive : colors.textSecondary

  const row = (
    <Container
      ref={triggerRef}
      items={hoverActions ?? []}
      disableHoverActions={!hoverActions?.length}
      testID={testId}
      onPress={onPress}
      disabled={disabled}
      isHovered={isHovered}
      setIsHovered={(hovered) => {
        setIsHovered(hovered)
        hovered ? tooltipRef.current?.show() : tooltipRef.current?.hide()
      }}
      ariaDescribedBy={showDisabledTooltip ? disabledDescriptionId : undefined}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          tooltipRef.current?.hide()
        }
      }}
    >
      <LeftSection>
        <IconWrapper disabled={disabled}>{icon}</IconWrapper>
        <TextSection>
          {taskType ? <BodyText text={taskType} size={taskTypeSize} color={secondaryTextColor} /> : null}
          <BodyText text={title} color={primaryTextColor} />
        </TextSection>
      </LeftSection>
      {rightText ? (
        <RightSection>
          <BodyText text={rightText} size={rightTextSize} color={secondaryTextColor} />
        </RightSection>
      ) : null}
    </Container>
  )

  // Always a fragment so the row keeps its position, and its state, when `disabled` toggles
  const item = (
    <>
      {showDisabledTooltip ? (
        <>
          <Tooltip
            ref={tooltipRef}
            asChild
            hoverEnabled
            content={disabledTooltipContent}
            placement='top'
            testID={testId}
          >
            {/* Hidden from assistive tech and the tab order so it isn't a second button */}
            <View aria-hidden focusable={false} style={TOOLTIP_ANCHOR_STYLE} />
          </Tooltip>
          <DisabledDescription nativeID={disabledDescriptionId}>
            <BodyText text={disabledTooltipContent} />
          </DisabledDescription>
        </>
      ) : null}
      {row}
    </>
  )

  if (popover?.content) {
    return (
      <>
        {item}
        <Popover
          title={popover.title}
          trigger={triggerRef}
          isOpen={popover.isOpen}
          onOpenChange={popover.onOpenChange}
          placement={popover.position}
          positionKey={popover.positionKey}
        >
          {popover.content}
        </Popover>
      </>
    )
  }

  return item
}
